import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  signal,
} from '@angular/core';
import { AssetDto, GalleryDto } from '@nusign/picflow-backend-types';
import { firstValueFrom } from 'rxjs';
import { GALLERY_DOWNLOAD_SIZE } from '../../../consts';
import { ArchiveSize } from '../../../interfaces/archive-request.interface';
import { convertGalleryDownloadSize } from '../../../lib/convert-gallery-download-size';
import { estimateResizedAssetSize } from '../../../lib/estimate-resized-asset-size';
import { forkJoinSafe } from '../../../lib/safe-fork-join';
import { ArchiveService } from '../../../services/archive.service';
import { AssetService } from '../../../services/asset.service';
import { GalleryService } from '../../../services/gallery.service';
import { ButtonMenuItemComponent } from '../../../shared/components/button-menu-item/button-menu-item.component';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { CapitalizePipe } from '../../../shared/pipes/capitalize.pipe';
import { DataUnitsBigIntPipe } from '../../../shared/pipes/data-units-big-int.pipe';

@Component({
  selector: 'app-gallery-download-menu-options',
  templateUrl: './gallery-download-menu-options.component.html',
  styleUrls: ['./gallery-download-menu-options.component.scss'],
  imports: [ButtonMenuItemComponent, DataUnitsBigIntPipe, CapitalizePipe, LoadingIndicatorComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GalleryDownloadMenuOptionsComponent implements OnInit {
  private check = inject(ChangeDetectorRef);
  private galleryService = inject(GalleryService);
  private archiveService = inject(ArchiveService);
  private assetService = inject(AssetService);
  @Output() onDownload = new EventEmitter();
  @Input() gallery: GalleryDto;
  @Input() includeSubGalleries: boolean;
  @Input() assets: AssetDto[];
  availableSizes = signal<ArchiveSize[]>(undefined);
  sizes: ArchiveSize[] = ['small', 'medium', 'large', 'original'];
  sizesEstimates = signal<{ [size: string]: bigint }>({});

  private async getGalleries() {
    if (!this.includeSubGalleries) return [this.gallery];
    const subGalleries = await firstValueFrom(this.galleryService.getSubGalleries(this.gallery.id));
    return [this.gallery, ...subGalleries.filter(g => g.download_full_gallery)];
  }

  private async getAssets(galleries: GalleryDto[], size: ArchiveSize) {
    if (this.assets) return this.archiveService.filterAssets(this.assets, this.gallery, size);
    return (
      await Promise.all(
        galleries.map(async gallery => {
          const assets = await firstValueFrom(
            this.assetService.getAssetsForGalleryObservable(gallery.id, { sorted: true })
          );
          return this.archiveService.filterAssets(assets, gallery, size);
        })
      )
    ).reduce((all, assets) => [...all, ...assets], []);
  }

  async ngOnInit() {
    const galleries = await this.getGalleries();
    const maxLongEdges = await firstValueFrom(
      forkJoinSafe(galleries.map(g => this.archiveService.getMaxLongEdgeForGallery(g.id)))
    );
    const maxLongEdge = Math.max(...maxLongEdges);
    const enabledSizesForGalleries = galleries.map(g => convertGalleryDownloadSize(g.download_media_size));
    const enabledSizes = enabledSizesForGalleries[0];
    enabledSizesForGalleries.forEach(enabledSizesForGallery => {
      Object.keys(enabledSizes).forEach(size => {
        if (!enabledSizesForGallery[size]) enabledSizes[size] = false;
      });
    });

    const availableSizes: ArchiveSize[] = [];
    const sizesEstimates: { [size: string]: bigint } = {};
    await Promise.all(
      this.sizes.map(async size => {
        const assets = await this.getAssets(galleries, size);

        if (maxLongEdge < GALLERY_DOWNLOAD_SIZE[size] && size !== 'small') return;
        if (!enabledSizes[size]) return;

        if (size === 'original') {
          const allAssetsAreWatermarked = assets.every(
            asset => asset.watermark_enabled && asset.version_data.watermark_uuid
          );
          if (allAssetsAreWatermarked) return;

          sizesEstimates[size] = assets.reduce((total, asset) => total + BigInt(asset.version_data.size ?? 0), 0n);
        } else {
          sizesEstimates[size] = assets.reduce(
            (total, asset) => total + estimateResizedAssetSize(asset.version_data, GALLERY_DOWNLOAD_SIZE[size]),
            0n
          );
        }

        if (sizesEstimates[size] > 0) availableSizes.push(size);
      })
    );
    this.sizesEstimates.set(sizesEstimates);
    this.availableSizes.set(availableSizes);
  }
}
